import React, { useState, useEffect, useRef, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import API_ENDPOINTS, { IMAGE_BASE_URL } from "../../api/apiConfig";
import TrackingModal from './TrackingModal';
import 'bootstrap-icons/font/bootstrap-icons.css';
import type { Order, OrderItem, Pagination } from "../../types";
import "../../assets/css/theme.css";
import "../../assets/css/shop.css";     // shared page-header band (.ffs-hero*)
import "../../assets/css/orders.css";

const PLACEHOLDER_IMG =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='88' height='116'%3E%3Crect fill='%23eee6d8' width='88' height='116'/%3E%3C/svg%3E";

// -----------------------------------------------------------------
// Rating Modal
// -----------------------------------------------------------------
interface RatingModalProps {
    item: OrderItem;
    orderId: number | string;
    onClose: () => void;
    onSuccess: (productName: string) => void;
}

interface RatingTarget {
    item: OrderItem;
    orderId: number | string;
}

const RatingModal = ({ item, orderId, onClose, onSuccess }: RatingModalProps) => {
    const [selectedRating, setSelectedRating] = useState(0);
    const [hoveredRating, setHoveredRating]   = useState(0); 
    const [reviewText, setReviewText]         = useState("");
    const [submitting, setSubmitting]         = useState(false);
    const [error, setError]                   = useState<string>("");

    const token = localStorage.getItem("authToken");

    const ratingLabels = ["", "Terrible", "Bad", "Okay", "Good", "Excellent"];
    const ratingEmojis = ["", "😞", "😕", "😐", "😊", "🤩"];
    const ratingColors = ["", "#8b2020", "#a05020", "#7a5c12", "#1f6645", "#1b5e3b"];

    const handleSubmit = async () => {
        if (selectedRating === 0) { setError("Please select a star rating."); return; }
        setError("");
        setSubmitting(true);
        try {
            const res  = await fetch(API_ENDPOINTS.SUBMIT_PRODUCT_REVIEW, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    product_id: item.product_id,
                    order_id:   orderId,
                    rating:     selectedRating,
                    review:     reviewText.trim(),
                }),
            });
            const data = await res.json();
            if (data.status === "success") { onSuccess(item.product_name); onClose(); }
            else setError(data.message || "Failed to submit review.");
        } catch { setError("Something went wrong. Please try again."); }
        finally  { setSubmitting(false); }
    };

    const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => { if (e.target === e.currentTarget) onClose(); };
    const active = hoveredRating || selectedRating;

    return (
        <div className="ffo-modal-backdrop" onClick={handleBackdropClick}>
            <div className="ffo-modal" role="dialog" aria-modal="true" aria-label="Rate product">
                <div className="ffo-modal-head">
                    <img
                        className="ffo-modal-thumb"
                        src={`${IMAGE_BASE_URL}/media/${item.product_image}`}
                        alt={item.product_name}
                        onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = PLACEHOLDER_IMG;
                        }}
                    />
                    <div>
                        <div className="ffo-modal-eyebrow">Rate product</div>
                        <div className="ffo-modal-title">{item.product_name}</div>
                    </div>
                    <button type="button" className="ffo-modal-close" onClick={onClose} aria-label="Close">✕</button>
                </div>

                <div className="ffo-modal-body">
                    <p className="ffo-question">How would you rate this product?</p>
                    <div className="ffo-stars" onMouseLeave={() => setHoveredRating(0)}>
                        {[1, 2, 3, 4, 5].map((s) => (
                            <button
                                key={s}
                                type="button"
                                className={`ffo-star${s <= active ? " is-on" : ""}`}
                                onClick={() => setSelectedRating(s)}
                                onMouseEnter={() => setHoveredRating(s)}
                                aria-label={`${s} star${s > 1 ? "s" : ""}`}
                            >★</button>
                        ))}
                    </div>
                    {selectedRating > 0 ? (
                        <div className="ffo-rating-chip" style={{ color: ratingColors[selectedRating] }}>
                            {ratingLabels[selectedRating]} {ratingEmojis[selectedRating]}
                        </div>
                    ) : (
                        <div className="ffo-rating-chip">&nbsp;</div>
                    )}

                    <label className="ffo-label" htmlFor="ffo-review">
                        Write a review <span>(optional)</span>
                    </label>
                    <textarea
                        id="ffo-review"
                        className="ffo-textarea"
                        value={reviewText}
                        onChange={(e) => setReviewText(e.target.value)}
                        maxLength={300}
                        rows={3}
                        placeholder="Share your thoughts about this product…"
                    />
                    <div className="ffo-char">{reviewText.length} / 300</div>
                    {error && <div className="ffo-error" role="alert">{error}</div>}
                </div>

                <div className="ffo-modal-foot">
                    <button type="button" className="ffh-btn ffh-btn--outline" onClick={onClose} disabled={submitting}>Cancel</button>
                    <button type="button" className="ffh-btn" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? (<><span className="ffo-spin" /> Submitting…</>) : "Submit review"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// -----------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------
const isDelivered = (s?: string): boolean => ["DELIVERED", "COMPLETED"].includes(s?.toUpperCase() as string);

const badgeClass = (s?: string): string => {
    switch (s?.toUpperCase()) {
        case "PENDING":    return "ffo-badge--pending";
        case "DELIVERED":  return "ffo-badge--delivered";
        case "COMPLETED":  return "ffo-badge--completed";
        case "CANCELLED":
        case "CANCELED":   return "ffo-badge--cancelled";
        case "PROCESSING": return "ffo-badge--processing";
        case "SHIPPED":    return "ffo-badge--shipped";
        default:           return "ffo-badge--default";
    }
};

// Only icons confirmed working in original project
const statusIcon = (s?: string): string => {
    switch (s?.toUpperCase()) {
        case "PENDING":    return "bi-hourglass";
        case "DELIVERED":
        case "COMPLETED":  return "bi-bag-check";
        case "CANCELLED":
        case "CANCELED":   return "bi-x-circle";
        case "PROCESSING": return "bi-gear";
        case "SHIPPED":    return "bi-truck";
        default:           return "bi-circle";
    }
};

const showTrack = (order: Order) => {
    const s = order.status?.toUpperCase();
    return order.awb_no && s !== "CANCELLED" && s !== "CANCELED";
};

const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString("en-IN", {
        day: "numeric", month: "short", year: "numeric",
    });

const fmtTime = (d: string) =>
    new Date(d).toLocaleTimeString("en-IN", {
        hour: "numeric", minute: "2-digit", hour12: true,
    });
// -----------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------
const MyOrdersScreen = () => {
    const [orders, setOrders]             = useState<Order[]>([]);
    const [loading, setLoading]           = useState(true);
    const [error, setError]               = useState<string>("");
    const [showTracking, setShowTracking] = useState(false);
    const [selOrder, setSelOrder]         = useState<Order | null>(null);
    const [ratingModal, setRatingModal]   = useState<RatingTarget | null>(null);
    const [successMsg, setSuccessMsg]     = useState("");
    const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [currentPage, setCurrentPage]   = useState(1);
    const [pagination, setPagination]     = useState<Pagination | null>(null);
    const [perPage, setPerPage]     = useState(10);
    const [expanded, setExpanded]   = useState<Set<number | string>>(new Set());

    const token = localStorage.getItem("authToken");

    const fetchOrders = async (page = 1) => {
        setLoading(true); setError("");
        try {
            const headers: Record<string, string> = { "Content-Type": "application/json" };
            if (token) headers["Authorization"] = `Bearer ${token}`;
            const res  = await fetch(`${API_ENDPOINTS.GET_USER_ORDER_PAGINATION}?page=${page}&per_page=${perPage}`, { headers });
            const data = await res.json();
            if (res.ok && data.status === "success" && Array.isArray(data.orders)) {
                setOrders(data.orders);
                // every order starts collapsed – tap a row to open it
                setExpanded(new Set());
                setPagination(data.pagination);
            } else {
                setOrders([]); setError(data.message || "Failed to fetch orders.");
            }
        } catch { setOrders([]); setError("An error occurred while fetching orders."); }
        finally  { setLoading(false); }
    };

    useEffect(() => {
        fetchOrders(currentPage);
        return () => clearTimeout(successTimer.current);
    }, [currentPage, perPage]);

    const handlePageChange = (page: number) => {
        if (page < 1 || (pagination && page > pagination.total_pages)) return;
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleReviewSuccess = (name: string) => {
        clearTimeout(successTimer.current);
        setSuccessMsg(`Review for "${name}" submitted!`);
        successTimer.current = setTimeout(() => setSuccessMsg(""), 4000);
    };

    const getPageNumbers = () => {
        if (!pagination) return [];
        const { total_pages: total, current_page: cur } = pagination;
        const pages: Array<number | string> = [];
        if (total <= 5) { for (let i = 1; i <= total; i++) pages.push(i); }
        else if (cur <= 3) { pages.push(1, 2, 3, 4, "...", total); }
        else if (cur >= total - 2) { pages.push(1, "...", total-3, total-2, total-1, total); }
        else { pages.push(1, "...", cur-1, cur, cur+1, "...", total); }
        return pages;
    };

    const toggleOrder = (id: number | string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });

    const allOpen = orders.length > 0 && orders.every((o) => expanded.has(o.order_id));
    const toggleAll = () =>
        setExpanded(allOpen ? new Set() : new Set(orders.map((o) => o.order_id)));

    const handlePerPage = (value: number) => {
        setPerPage(value);
        setCurrentPage(1);
    };

    const monthLabel = (d: string) =>
        new Date(d).toLocaleDateString("en-IN", { month: "long", year: "numeric" });

    const rangeStart = pagination ? (pagination.current_page - 1) * pagination.per_page + 1 : 0;
    const rangeEnd = pagination ? Math.min(pagination.current_page * pagination.per_page, pagination.total_count) : 0;

    const statusKey = (s?: string) => badgeClass(s).replace("ffo-badge--", "");

    return (
        <main className="ffo-page">

            {/* -- Toast -- */}
            {successMsg && (
                <div className="ffo-toast" role="status">
                    <i className="bi bi-bag-check" />
                    {successMsg}
                </div>
            )}

            {/* -- Header band -- */}
            <header className="ffs-hero">
                <div className="ffh-container ffs-hero-inner">
                    <div>
                        <nav className="ffs-crumbs" aria-label="Breadcrumb">
                            <Link to="/">Home</Link>
                            <span aria-hidden="true">/</span>
                            <Link to="/profile">My account</Link>
                            <span aria-hidden="true">/</span>
                            <span className="is-current">Order history</span>
                        </nav>
                        <h1 className="ffs-hero-title">Order <em>History</em></h1>
                    </div>
                    {pagination && (
                        <span className="ffo-count">
                            {pagination.total_count} {pagination.total_count === 1 ? "order" : "orders"}
                        </span>
                    )}
                </div>
            </header>

            {/* -- Orders -- */}
            <div className="ffh-container">
                {loading ? (
                    <div className="ffo-list" aria-busy="true">
                        <div className="ffo-skel" />
                        <div className="ffo-skel" />
                    </div>
                ) : error ? (
                    <div className="ffo-state ffo-state--error">
                        <span className="ffo-state-icon" aria-hidden="true"><i className="bi bi-exclamation-triangle" /></span>
                        <h2>Something went wrong</h2>
                        <p>{error}</p>
                        <button type="button" className="ffh-btn" onClick={() => fetchOrders(currentPage)}>Try again</button>
                    </div>
                ) : orders.length === 0 ? (
                    <div className="ffo-state">
                        <span className="ffo-state-icon" aria-hidden="true"><i className="bi bi-bag" /></span>
                        <h2>No orders yet</h2>
                        <p>You haven't placed any orders yet.</p>
                        <Link to="/products" className="ffh-btn">Start shopping</Link>
                    </div>
                ) : (
                    <>
                        {/* -- Toolbar -- */}
                        <div className="ffo-toolbar">
                            <p className="ffo-range">
                                Showing <strong>{rangeStart}–{rangeEnd}</strong> of {pagination?.total_count ?? orders.length} orders
                            </p>
                            <div className="ffo-toolbar-actions">
                                <button type="button" className="ffo-link" onClick={toggleAll}>
                                    {allOpen ? "Collapse all" : "Expand all"}
                                </button>
                                <label className="ffo-perpage">
                                    <span>Show</span>
                                    <select value={perPage} onChange={(e) => handlePerPage(Number(e.target.value))}>
                                        {[10, 20, 50].map((n) => (
                                            <option key={n} value={n}>{n}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>

                        <div className="ffo-list">
                            {orders.map((order, oi) => {
                                const delivered = isDelivered(order.status);
                                const hasCoupon = order.coupon_code && order.coupon_code.trim() !== "";
                                const isOpen = expanded.has(order.order_id);
                                const items = order.items || [];
                                const month = monthLabel(order.created_at);
                                const showMonth = oi === 0 || month !== monthLabel(orders[oi - 1].created_at);
                                const firstName = items[0]?.product_name || "Order";
                                const extra = items.length - 1;

                                return (
                                    <React.Fragment key={order.order_id}>
                                        {showMonth && <h3 className="ffo-month">{month}</h3>}

                                        <article
                                            className={`ffo-card ffo-card--${statusKey(order.status)}${isOpen ? " is-open" : ""}`}
                                            style={{ animationDelay: `${Math.min(oi, 8) * 0.04}s` }}
                                        >
                                            {/* -- Summary row (always visible) -- */}
                                            <button
                                                type="button"
                                                className="ffo-row"
                                                onClick={() => toggleOrder(order.order_id)}
                                                aria-expanded={isOpen}
                                                aria-controls={`ffo-order-${order.order_id}`}
                                            >
                                                <span className="ffo-stack" aria-hidden="true">
                                                    {items.slice(0, 3).map((it, i) => (
                                                        <img
                                                            key={i}
                                                            src={`${IMAGE_BASE_URL}/media/${it.product_image}`}
                                                            alt=""
                                                            onError={(e) => {
                                                                e.currentTarget.onerror = null;
                                                                e.currentTarget.src = PLACEHOLDER_IMG;
                                                            }}
                                                        />
                                                    ))}
                                                    {items.length > 3 && <b>+{items.length - 3}</b>}
                                                </span>

                                                <span className="ffo-row-main">
                                                    <span className="ffo-order-id">Order #{order.order_id}</span>
                                                    <span className="ffo-datetime">
                                                        <span><i className="bi bi-calendar" />{fmtDate(order.created_at)}</span>
                                                        <span aria-hidden="true">·</span>
                                                        <span><i className="bi bi-clock" />{fmtTime(order.created_at)}</span>
                                                    </span>
                                                    <span className="ffo-row-items">
                                                        {firstName}{extra > 0 ? ` + ${extra} more` : ""}
                                                    </span>
                                                </span>

                                                <span className="ffo-row-total">
                                                    <small>Total</small>
                                                    ₹{order.total_amount?.toFixed(2) || "0.00"}
                                                </span>

                                                <span className={`ffo-badge ${badgeClass(order.status)}`}>
                                                    <i className={`bi ${statusIcon(order.status)}`} />
                                                    {order.status?.toUpperCase() || "N/A"}
                                                </span>

                                                <span className="ffo-chevron" aria-hidden="true">
                                                    <i className="bi bi-chevron-down" />
                                                </span>
                                            </button>

                                            {/* -- Details (expanded) -- */}
                                            {isOpen && (
                                                <div className="ffo-card-body" id={`ffo-order-${order.order_id}`}>
                                                    {/* -- Items -- */}
                                                    <div className="ffo-items">
                                                        <div className="ffo-items-head">
                                                            <i className="bi bi-bag" />
                                                            <span>Items ordered</span>
                                                            <b>{items.length}</b>
                                                        </div>

                                                        {items.map((item, idx) => (
                                                            <div key={idx} className="ffo-item">
                                                                <div className="ffo-thumb">
                                                                    <img
                                                                        src={`${IMAGE_BASE_URL}/media/${item.product_image}`}
                                                                        alt={item.product_name}
                                                                        onError={(e) => {
                                                                            e.currentTarget.onerror = null;
                                                                            e.currentTarget.src = PLACEHOLDER_IMG;
                                                                        }}
                                                                    />
                                                                    {item.discount_percentage > 0 && (
                                                                        <span className="ffo-thumb-off">-{item.discount_percentage}%</span>
                                                                    )}
                                                                </div>

                                                                <div className="ffo-item-info">
                                                                    <div className="ffo-item-name">{item.product_name}</div>
                                                                    <div className="ffo-chips">
                                                                        {item.size && (
                                                                            <span className="ffo-chip"><i className="bi bi-rulers" />{item.size}</span>
                                                                        )}
                                                                        <span className="ffo-chip"><i className="bi bi-bag" />Qty {item.quantity}</span>
                                                                    </div>
                                                                    <div className="ffo-price-row">
                                                                        <span className="ffo-price">₹{item.price?.toFixed(2) || "0.00"}</span>
                                                                        {item.discount_percentage > 0 && (
                                                                            <span className="ffo-off">{item.discount_percentage}% OFF</span>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {delivered && (
                                                                    <button
                                                                        type="button"
                                                                        className="ffo-rate"
                                                                        onClick={() => setRatingModal({ item, orderId: order.uo_id || order.order_id })}
                                                                        title="Rate this product"
                                                                    >
                                                                        <i className="bi bi-star" /> Rate
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* -- Summary -- */}
                                                    <aside className="ffo-summary">
                                                        <div className="ffo-kv">
                                                            <span>Payment</span>
                                                            <span>{order.payment_method || "N/A"}</span>
                                                        </div>
                                                        <div className="ffo-kv">
                                                            <span>Payment status</span>
                                                            <span>{order.payment_status || "N/A"}</span>
                                                        </div>

                                                        {hasCoupon && (
                                                            <div className="ffo-coupon">
                                                                <i className="bi bi-tag-fill" />
                                                                <div>
                                                                    <strong>{order.coupon_code}</strong>
                                                                    <span>
                                                                        {Number(order.coupon_discount_rate || 0).toFixed(0)}% off · saved ₹{Number(order.discount_amount || 0).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {order.address && (
                                                            <div className="ffo-address">
                                                                <div className="ffo-address-label">Delivering to</div>
                                                                <strong>{order.address.full_name}</strong>
                                                                <span>
                                                                    {order.address.address_line}, {order.address.city},{" "}
                                                                    {order.address.state} - {order.address.postal_code}
                                                                </span>
                                                            </div>
                                                        )}

                                                        <div className="ffo-actions">
                                                            {showTrack(order) && (
                                                                <button
                                                                    type="button"
                                                                    className="ffh-btn"
                                                                    onClick={() => { setSelOrder(order); setShowTracking(true); }}
                                                                >
                                                                    <i className="bi bi-truck" /> Track order
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="ffh-btn ffh-btn--outline"
                                                                onClick={() => window.open(`${API_ENDPOINTS.INVOICEDOWNLOAD}${order.uo_id || order.order_id}/invoice/`, "_blank")}
                                                            >
                                                                <i className="bi bi-download" /> Invoice
                                                            </button>
                                                        </div>
                                                    </aside>
                                                </div>
                                            )}
                                        </article>
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {/* -- Pagination -- */}
                        {pagination && pagination.total_pages > 1 && (
                            <nav className="ffo-pag" aria-label="Order pagination">
                                <button
                                    type="button"
                                    className="ffo-pag-btn ffo-pag-btn--nav"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={!pagination.has_previous}
                                >
                                    ← Prev
                                </button>
                                <div className="ffo-pag-nums">
                                    {getPageNumbers().map((page, idx) =>
                                        page === "..." ? (
                                            <span key={`e-${idx}`} className="ffo-pag-dots">…</span>
                                        ) : (
                                            <button
                                                key={page}
                                                type="button"
                                                className={`ffo-pag-btn${page === currentPage ? " is-active" : ""}`}
                                                onClick={() => handlePageChange(page as number)}
                                            >
                                                {page}
                                            </button>
                                        )
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="ffo-pag-btn ffo-pag-btn--nav"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={!pagination.has_next}
                                >
                                    Next →
                                </button>
                            </nav>
                        )}
                    </>
                )}
            </div>

            {showTracking && selOrder && (
                <TrackingModal
                    awbCode={selOrder.awb_no}
                    orderId={selOrder.order_id}
                    onClose={() => { setShowTracking(false); setSelOrder(null); }}
                />
            )}

            {ratingModal && (
                <RatingModal
                    item={ratingModal.item}
                    orderId={ratingModal.orderId}
                    onClose={() => setRatingModal(null)}
                    onSuccess={handleReviewSuccess}
                />
            )}
        </main>
    );
};

export default MyOrdersScreen;